import { useContext } from 'react';

import { AppContext } from '@/app.context';

import { isStandAlone } from '../ui/installation';
import { isSafari } from '../ui/installation-dialog';
import { promptInstall } from './prompt-install';
import { useInstallDialog } from './use-install-dialog';

/**
 * The install affordance's gating + click behaviour, in one place --
 * previously duplicated between `basic`'s home `Topbar` (its own `InstallBtn`
 * usage) and `HomeMenu` (a hand-rolled `InstallButtonProps` object), each
 * combining `isInstalled`/`isStandAlone`/`isSupportInstallPrompt` slightly
 * differently. Any menu wanting an Install entry -- `AppMenu`, the issue
 * reader's own menu -- calls this instead of re-deriving the same checks.
 *
 * `onClick` prefers the real native prompt (`deferredPrompt`, Chrome/
 * Android) over `InstallationDialog`'s manual instructions, falling back to
 * the dialog only when there's no captured prompt to trigger -- iOS Safari,
 * notably, never fires `beforeinstallprompt` at all.
 */
export function useInstallAction(): UseInstallActionResult {
	const { isInstalled, isSupportInstallPrompt, deferredPrompt } =
		useContext(AppContext);
	const { setShowInstallation } = useInstallDialog();

	const show =
		!isInstalled &&
		!isStandAlone() &&
		(isSafari() || isSupportInstallPrompt);

	const onClick = () => {
		if (deferredPrompt) {
			void promptInstall(deferredPrompt);
			return;
		}
		setShowInstallation(true);
	};

	return { show, onClick };
}

export interface UseInstallActionResult {
	show: boolean;
	onClick: () => void;
}
